import React, { useState } from 'react';
import { Info, AlertTriangle, XCircle, Ban, X } from 'lucide-react';

import { ConductRule } from '../../types';
import { CONDUCT_RULES } from '../../constants';
import { useTranslation } from '../../i18n/hooks/useTranslation';


const ConductRulesMatrix = () => {
  const { t } = useTranslation();

  const [selectedCell, setSelectedCell] = useState<{
    rule: ConductRule;
    type: string;
    content: string;
  } | null>(null);

  // Utilisation des constantes importées
  const conductRules = CONDUCT_RULES;

  const filteredRules = conductRules;

  const openCellDetail = (rule: ConductRule, type: string, content: string) => {
    if (content.trim()) {
      setSelectedCell({ rule, type, content });
    }
  };

  const getCellClass = (content: string, type: string, isGameSpan = false) => {
    const baseClass = "p-2 text-xs cursor-pointer hover:bg-opacity-80 transition-colors min-h-[60px] flex items-center";
    const hasContent = content && content.trim();
    
    if (!hasContent) {
      return `${baseClass} bg-gray-100 text-gray-400 border-r border-gray-300`;
    }

    switch (type) {
      case 'minor':
        return `${baseClass} bg-yellow-100 text-yellow-900 border-yellow-200 border-r border-gray-300`;
      case 'major':
        return `${baseClass} bg-orange-100 text-orange-900 border-orange-200 border-r border-gray-300`;
      case 'game':
        // Dégradé rouge renforcé et plus visible
        return `${baseClass} bg-gradient-to-r from-red-200 to-red-400 text-red-900 border-red-300 shadow-inner`;
      case 'match':
        return `${baseClass} bg-red-200 text-red-950 border-red-300 border-r border-gray-300`;
      default:
        return `${baseClass} bg-gray-50 border-r border-gray-300`;
    }
  };

  const getSeverityLabel = (type) => {
    switch (type) {
      case 'minor': return t('conduct.severity.warning');
      case 'major': return t('conduct.severity.strokePenalty');
      case 'game': return t('conduct.severity.gamePenalty');
      case 'match': return t('conduct.severity.matchPenalty');
      default: return '';
    }
  };

  const getSeverityIcon = (type) => {
    switch (type) {
      case 'minor': return <Info className="w-4 h-4" />;
      case 'major': return <AlertTriangle className="w-4 h-4" />;
      case 'game': return <XCircle className="w-4 h-4" />;
      case 'match': return <Ban className="w-4 h-4" />;
      default: return null;
    }
  };

  return (
    <div className="w-full max-w-7xl mx-auto p-4 bg-white">

      {/* Escalation Notice */}
      <div className="mb-6 p-3 bg-blue-50 border border-blue-200">
        <div className="text-sm text-blue-800 text-center">
          <span className="font-semibold">{t('conduct.escalation.title')}:</span>
          <span className="mx-2">{t('conduct.escalation.first')}</span>
          <span className="mx-2">•</span>
          <span className="mx-2">{t('conduct.escalation.second')}</span>
          <span className="mx-2">•</span>
          <span className="mx-2">{t('conduct.escalation.third')} <span className="font-semibold">{t('conduct.escalation.mandatory')}</span></span>
        </div>
      </div>

      {/* Matrix Table */}
      <div className="border border-gray-300 overflow-hidden bg-white">
        {/* Desktop Version */}
        <div className="hidden md:block">
          {/* Table Header */}
          <div className="bg-gray-600 text-white">
            <div className="grid grid-cols-5">
              <div className="p-3 text-sm font-semibold border-r border-gray-500">
                {t('conduct.matrix.offense')}
              </div>
              <div className="p-3 text-sm font-semibold border-r border-gray-500 bg-yellow-600 text-center">
                <div className="flex items-center justify-center gap-2">
                  <Info className="w-4 h-4" />
                  {t('conduct.matrix.minorOffense')}
                </div>
                <div className="mt-1 text-xs font-normal">{t('conduct.severity.warning')}</div>
              </div>
              <div className="p-3 text-sm font-semibold border-r border-gray-500 bg-orange-600 text-center">
                <div className="flex items-center justify-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  {t('conduct.matrix.majorOffense')}
                </div>
                <div className="mt-1 text-xs font-normal">{t('conduct.severity.strokePenalty')}</div>
              </div>
              <div className="p-3 text-sm font-semibold border-r border-gray-500 bg-red-600 text-center">
                <div className="flex items-center justify-center gap-2">
                  <XCircle className="w-4 h-4" />
                  {t('conduct.matrix.severeOffense')}
                </div>
                <div className="mt-1 text-xs font-normal">{t('conduct.severity.gamePenalty')}</div>
              </div>
              <div className="p-3 text-sm font-semibold border-r border-gray-500 bg-red-600 text-center">
                <div className="flex items-center justify-center gap-2">
                  <Ban className="w-4 h-4" />
                  {t('conduct.matrix.severeOffense')}
                </div>
                <div className="mt-1 text-xs font-normal">{t('conduct.severity.matchPenalty')}</div>
              </div>

            </div>
          </div>

          {/* Desktop Table Body */}
          {filteredRules.map((rule, index) => (
            <div key={rule.id} className={`border-b border-gray-200 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-25'}`}>
              <div className="grid grid-cols-5">
                {/* Rule Title */}
                <div className="p-3 border-r border-gray-300 bg-gray-50">
                  <div className="font-semibold text-sm text-blue-600 mb-1">{rule.id}</div>
                  <div className="text-sm text-gray-900 leading-tight">{rule.title}</div>
                </div>

                {/* Minor Infraction */}
                <div 
                  className={getCellClass(rule.minor, 'minor')}
                  onClick={() => openCellDetail(rule, 'minor', rule.minor)}
                >
                  <div className="line-clamp-4 leading-tight">
                    {rule.minor.split('\n').map((line, i) => (
                      <div key={i} className="mb-1 last:mb-0">
                        {line.startsWith('-') ? (
                          <div className="text-xs">• {line.substring(1).trim()}</div>
                        ) : (
                          <div className="text-xs">{line}</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Major Infraction */}
                <div 
                  className={getCellClass(rule.major, 'major')}
                  onClick={() => openCellDetail(rule, 'major', rule.major)}
                >
                  <div className="line-clamp-4 leading-tight">
                    {rule.major.split('\n').map((line, i) => (
                      <div key={i} className="mb-1 last:mb-0">
                        {line.startsWith('-') ? (
                          <div className="text-xs">• {line.substring(1).trim()}</div>
                        ) : (
                          <div className="text-xs">{line}</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Game/Match Infraction - Spans 2 columns if has content */}
                {rule.game && rule.game.trim() ? (
                  <div 
                    className={`${getCellClass(rule.game, 'game')} col-span-2`}
                    onClick={() => openCellDetail(rule, 'game', rule.game)}
                  >
                    <div className="line-clamp-4 leading-tight">
                      {rule.game.split('\n').map((line, i) => (
                        <div key={i} className="mb-1 last:mb-0">
                          {line.startsWith('-') ? (
                            <div className="text-xs">• {line.substring(1).trim()}</div>
                          ) : (
                            <div className="text-xs">{line}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Empty Game Infraction */}
                    <div className={getCellClass('', 'game')}>
                      <div className="text-xs text-gray-400">—</div>
                    </div>
                    {/* Empty Match Infraction */}
                    <div className={getCellClass('', 'match')}>
                      <div className="text-xs text-gray-400">—</div>
                    </div>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Mobile Version - Card Layout */}
        <div className="md:hidden">
          {/* Mobile Legend */}
          <div className="bg-gray-100 p-3 grid grid-cols-2 gap-2 text-xs">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-yellow-400"></div>
              <span>{t('conduct.legend.warning')}</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-orange-400"></div>
              <span>{t('conduct.legend.stroke')}</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-red-400"></div>
              <span>{t('conduct.legend.game')}</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-red-600"></div>
              <span>{t('conduct.legend.match')}</span>
            </div>
          </div>

          {/* Mobile Rules Cards */}
          <div className="divide-y divide-gray-200">
            {filteredRules.map((rule, index) => (
              <div key={rule.id} className="p-3">
                {/* Rule Header */}
                <div className="mb-3">
                  <div className="font-semibold text-sm text-blue-600">{rule.id}</div>
                  <div className="text-sm text-gray-900 font-medium">{rule.title}</div>
                </div>

                {/* Sanctions Grid */}
                <div className="space-y-2">
                  {/* Minor */} 
                  {rule.minor && (
                    <div
                      className="p-2 bg-yellow-100 border-l-4 border-yellow-400 cursor-pointer"
                      onClick={() => openCellDetail(rule, 'minor', rule.minor)}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <Info className="w-3 h-3 text-yellow-600" />
                        <span className="text-xs font-medium text-yellow-800">{t('conduct.legend.warning')}</span>
                      </div>
                      <div className="text-xs text-yellow-900 line-clamp-2">{rule.minor}</div>
                    </div>
                  )}

                  {/* Major */}
                  {rule.major && (
                    <div
                      className="p-2 bg-orange-100 border-l-4 border-orange-400 cursor-pointer"
                      onClick={() => openCellDetail(rule, 'major', rule.major)}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <AlertTriangle className="w-3 h-3 text-orange-600" />
                        <span className="text-xs font-medium text-orange-800">{t('conduct.legend.stroke')}</span>
                      </div>
                      <div className="text-xs text-orange-900 line-clamp-2">{rule.major}</div>
                    </div>
                  )}

                  {/* Game */}
                  {rule.game && rule.game.trim() && (
                    <div
                      className="p-2 bg-red-100 border-l-4 border-red-400 cursor-pointer"
                      onClick={() => openCellDetail(rule, 'game', rule.game)}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <XCircle className="w-3 h-3 text-red-600" />
                        <span className="text-xs font-medium text-red-800">{t('conduct.legend.gamematch')}</span>
                      </div>
                      <div className="text-xs text-red-900 line-clamp-2">{rule.game}</div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Mobile Responsive Note */}
      <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 md:hidden">
        <p className="text-sm text-yellow-800">
          💡 <strong>Tip :</strong> Touch a sanction for details
        </p>
      </div>

      {/* Cell Detail Modal */}
      {selectedCell && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-6 max-w-md w-full max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                {getSeverityIcon(selectedCell.type)}
                <h3 className="text-lg font-semibold text-gray-900">
                  {getSeverityLabel(selectedCell.type)}
                </h3>
              </div>
              <button
                onClick={() => setSelectedCell(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="mb-4">
              <div className="text-sm font-medium text-blue-600 mb-1">
                {selectedCell.rule.id}
              </div>
              <div className="text-base font-medium text-gray-900">
                {selectedCell.rule.title}
              </div>
            </div>

            <div className="space-y-2">
              {selectedCell.content.split('\n').map((line, i) => (
                <div key={i} className="text-sm text-gray-700">
                  {line.startsWith('-') ? (
                    <div className="flex items-start gap-2">
                      <span className="text-gray-400 mt-1">•</span>
                      <span>{line.substring(1).trim()}</span>
                    </div>
                  ) : (
                    <div>{line}</div>
                  )}
                </div>
              ))}
            </div>

            <button
              onClick={() => setSelectedCell(null)}
              className="mt-6 w-full bg-gray-500 text-white py-2 px-4 hover:bg-gray-600"
            >
              Fermer
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export { ConductRulesMatrix };